import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview TagPill Component
 *
 * Interactive tag component with removable functionality and keyboard navigation.
 * Supports disabled state and proper accessibility features.
 *
 * @author LOCI Development Team
 * @since 1.1.0
 */
import { forwardRef, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../theme';
/**
 * Size configurations for TagPill
 */
const SIZE_CONFIG = {
    xs: { height: 'h-6', padding: 'px-2 pl-2.5', fontSize: 'text-xs', gap: 'gap-1' },
    sm: { height: 'h-7', padding: 'px-2.5 pl-3', fontSize: 'text-sm', gap: 'gap-1' },
    md: { height: 'h-8', padding: 'px-3 pl-3.5', fontSize: 'text-sm', gap: 'gap-1.5' },
    lg: { height: 'h-9', padding: 'px-3.5 pl-4', fontSize: 'text-base', gap: 'gap-2' },
};
/**
 * Button size configurations with minimum 44px touch targets
 */
const BUTTON_SIZE_CONFIG = {
    xs: { size: 12, padding: 'p-1.5', minSize: 'min-w-[44px] min-h-[44px]' },
    sm: { size: 14, padding: 'p-2', minSize: 'min-w-[44px] min-h-[44px]' },
    md: { size: 16, padding: 'p-2', minSize: 'min-w-[44px] min-h-[44px]' },
    lg: { size: 18, padding: 'p-2.5', minSize: 'min-w-[44px] min-h-[44px]' },
};
/**
 * TagPill Component
 *
 * Interactive tag component with removable functionality and full accessibility support.
 */
export const TagPill = forwardRef(({ label, onRemove, disabled = false, size = 'md', variant = 'subtle', className = '', 'data-testid': testId, 'aria-label': ariaLabel, }, ref) => {
    const theme = useTheme();
    const removeButtonRef = useRef(null);
    const sizeConfig = SIZE_CONFIG[size];
    const buttonConfig = BUTTON_SIZE_CONFIG[size];
    const handleRemove = useCallback(() => {
        if (!disabled && onRemove) {
            onRemove();
        }
    }, [disabled, onRemove]);
    const handleKeyDown = useCallback((e) => {
        if (disabled)
            return;
        switch (e.key) {
            case 'Delete':
            case 'Backspace':
                e.preventDefault();
                handleRemove();
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                handleRemove();
                break;
            case 'Escape':
                // Blur the button without triggering removal
                removeButtonRef.current?.blur();
                break;
        }
    }, [disabled, handleRemove]);
    const baseClasses = [
        'inline-flex items-center',
        'font-medium rounded-full',
        'border transition-all duration-200',
        sizeConfig.height,
        sizeConfig.fontSize,
        sizeConfig.gap,
    ];
    const interactiveClasses = disabled
        ? 'opacity-50 cursor-not-allowed'
        : 'hover:bg-opacity-80 focus-within:bg-opacity-80 focus-within:ring-2 focus-within:ring-offset-1';
    const classes = [...baseClasses, interactiveClasses, className].filter(Boolean).join(' ');
    const styles = {
        backgroundColor: theme.colors.surface,
        color: theme.colors.text,
        borderColor: theme.colors.border,
    };
    const hoverStyles = disabled
        ? {}
        : {
            '--tw-ring-color': theme.colors.ring,
        };
    return (_jsxs("span", { ref: ref, className: classes, style: { ...styles, ...hoverStyles }, "data-testid": testId || `tag-pill-${label.toLowerCase().replace(/\s+/g, '-')}`, role: "group", "aria-label": ariaLabel || `Tag: ${label}${onRemove ? ', removable' : ''}`, children: [_jsx("span", { className: `${sizeConfig.padding} pr-0 whitespace-nowrap select-none flex-1`, children: label }), onRemove && (_jsx("button", { ref: removeButtonRef, type: "button", className: `
              flex items-center justify-center
              rounded-full
              transition-colors duration-150
              disabled:opacity-50 disabled:cursor-not-allowed
              focus:outline-none focus:ring-2 focus:ring-offset-1
              hover:bg-opacity-80 focus:bg-opacity-80
              ${buttonConfig.padding}
              ${buttonConfig.minSize}
            `, style: {
                    color: theme.colors.textMuted,
                    borderColor: theme.colors.ring,
                }, onClick: handleRemove, onKeyDown: handleKeyDown, disabled: disabled, "aria-label": `Remove ${label} tag`, "data-testid": `remove-${label.toLowerCase().replace(/\s+/g, '-')}`, children: _jsx(X, { size: buttonConfig.size, className: "flex-shrink-0", "aria-hidden": "true" }) }))] }));
});
TagPill.displayName = 'TagPill';
